/* eslint-disable react/jsx-props-no-spreading */

import React, { useState } from 'react';
import {
   Box,
   Button,
   LinearProgress,
   Table,
   TableBody,
   TableCell,
   TableHead,
   TableRow,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import ChildTable from './components/ChildTable';
import { StyledTableCell, useStyles } from './styles/index.style';
import CustomSortIcon from '../CustomSortIcon';
import { trimStartString } from '../../../functions';

const CustomTable = ({ columns, data, style, childColumns, childStyle, condensed, rowStyle }) => {
   const isPhone = useMediaQuery({ query: '(max-width: 768px)' });
   const classes = useStyles();

   const CellElement = ({ colIndex, rowIndex, cellValue }) => {
      if (colIndex === columns.length - 1 && childColumns.length) {
         return (
            <Box 
               display="flex" 
               justifyContent="end" 
               alignItems="center"
               style={{ padding: condensed ? '0px 2px' : '8px' }} 
               >
            {cellValue && cellValue !== '' ? cellValue : '-'}
            <Button
               style={{ 
                  marginLeft: "auto", visibility: 
                  columns[colIndex].showDropdown && columns[colIndex].showDropdown(rowIndex)? 'unset': 'hidden' }}
               onClick={() => {
                  columns[colIndex].onExpand(rowIndex);
               }}>
               {data[rowIndex].children?.expanded ? <KeyboardArrowUpIcon style={{ color: '#2A5CA9' }} /> : <KeyboardArrowDownIcon style={{ color: '#2A5CA9' }} />}
            </Button>
         </Box>)
      }
      return (<Box>{cellValue && cellValue !== '' ? cellValue : '-'}</Box>)
   }

   CellElement.propTypes = {
      colIndex: PropTypes.number,
      rowIndex: PropTypes.number,
      cellValue: PropTypes.node || PropTypes.string || PropTypes.number,
   };

   CellElement.defaultProps = {
      colIndex: 0,
      rowIndex: 0,
      cellValue: null,
   };

   const CustomRow = ({ row, rowIndex }) => (
      <React.Fragment key={rowIndex}>
         <TableRow
            key={`row-${rowIndex}`}
            style={{ 
               height: condensed ? '10px' : 'auto', 
               borderBottom: '1.5px solid #DDD',
               ...(rowStyle ? rowStyle(row) : {})
            }}
         >
            {columns.map((col, colIndex) => (
               <TableCell
                  style={{
                     padding: condensed ? '0px 2px' : '16px', 
                     borderBottom: 'unset',
                     ...(col.cellStyle ? col.cellStyle : {})
                  }}
               >
                  <CellElement
                     colIndex={colIndex}
                     rowIndex={rowIndex}
                     cellValue={col.cell ? col.cell(row) : row[col.key]} />
               </TableCell>
            ))}
         </TableRow>
         {(childColumns.length > 0) && row.children?.expanded && row.children?.loading && (
            <TableRow key={`row-${rowIndex}-loading`}>
               <StyledTableCell colSpan={columns.length} condensed={condensed}>
                  <LinearProgress
                     style={{ width: '100%' }}
                     classes={{
                        colorPrimary: classes.colorPrimary,
                        barColorPrimary: classes.barColorPrimary,
                     }}
                  />
               </StyledTableCell>
            </TableRow>)}
         {(childColumns.length > 0) && row.children?.expanded && !row.children?.loading && (<TableRow key={`row-${rowIndex}-child`}>
            <StyledTableCell colSpan={columns.length}  condensed={condensed}>
               <ChildTable columns={childColumns} data={row.children?.data} style={childStyle} />
            </StyledTableCell>
         </TableRow>)}
      </React.Fragment>
   )

   CustomRow.propTypes = {
      row: PropTypes.shape({
         children: PropTypes.shape({
            data: PropTypes.arrayOf(PropTypes.shape({})),
            loading: PropTypes.bool,
            expanded: PropTypes.bool,
         })
      }),
      rowIndex: PropTypes.number,
   };

   CustomRow.defaultProps = {
      row: {
         children: {
            data: [],
            loading: false,
            expanded: false,
         }
      },
      rowIndex: 0,
   };

   const CustomMobileRow = ({ row, rowIndex }) => {
      const [isFullView, setIsFullView] = useState();

      return (
         <Box
            key={rowIndex}
            className={classes.mobileCard}
            role="presentation"
            onClick={() => setIsFullView(!isFullView)}>

            {isFullView ? (<Box display="flex" flexDirection="column">
               {columns.map((col) => (<Box className={classes.flexLine}>
                  <Box className={classes.columnTitle}>
                     {col.header}
                  </Box>
                  <Box className={classes.columnValue}>
                     {row[col.key]}
                  </Box>
               </Box>))}
            </Box>) :
               (<Box className={classes.flexLineDefault} >
                  <Box className={classes.flexLineItem}>{row[columns[0].key]}</Box>
                  <Box className={classes.flexLineItem}>{row[columns[1].key] || '-'}</Box>
               </Box>)}
         </Box>
      )
   }

   CustomMobileRow.propTypes = {
      row: PropTypes.shape({
      }),
      rowIndex: PropTypes.number,
   };

   CustomMobileRow.defaultProps = {
      row: {},
      rowIndex: 0,
   };

   if (isPhone) {
      return (<Box>
         {data.map((row, rowIndex) => (
            <CustomMobileRow row={row} rowIndex={rowIndex} />))}
      </Box>)
   }

   return (
      <Table>
         <TableHead style={style?.tableHead || {}}>
            {columns.map((col) => (
               <TableCell
                  style={style?.headCell || {}}
                  {...col.headerProps}
               >
                  <Box display="flex" flex={1} justifyContent='space-between' alignItems='center'>
                     {col.header}
                     {col.sort?.sortable && (<Box onClick={() => col.sort.onClick(col.key)}>
                        <CustomSortIcon field={col.key} column={trimStartString(col.sort.selected || '', '-')} type={col.sort.selected === col.key ? 'asc' : 'desc'} />
                     </Box>)}
                  </Box>
               </TableCell>))}
         </TableHead>
         <TableBody>
            {data.length > 0? data.map((row, index) => (
               <CustomRow row={row} rowIndex={index} />)):
               (<TableRow>
                  <TableCell colSpan={columns.length}>
                     No Data Available
                  </TableCell>
               </TableRow>)}
         </TableBody>
      </Table>);
};

CustomTable.propTypes = {
   columns: PropTypes.arrayOf(PropTypes.obj),
   data: PropTypes.arrayOf(PropTypes.obj),
   style: PropTypes.shape({
      tableHead: PropTypes.shape({
      }),
      headCell: PropTypes.shape({
      }),
   }),
   childColumns: PropTypes.arrayOf(PropTypes.obj),
   childStyle: PropTypes.shape({
      tableHead: PropTypes.shape({
      }),
      headCell: PropTypes.shape({
      }),
   }),
   condensed: PropTypes.bool,
   rowStyle: PropTypes.func
};

CustomTable.defaultProps = {
   columns: [],
   data: [],
   style: {},
   childColumns: [],
   childStyle: {},
   condensed: true,
   rowStyle: null
};


export default CustomTable;